(() => {
  if (customElements.get('custom-sticky-atc')) return;

  class CustomStickyATC extends HTMLElement {
    connectedCallback() {
      if (this._isMounted) return;
      this._isMounted = true;

      this.$button = this.querySelector('[data-custom-sticky-atc-button]');
      this.$buttonText = this.querySelector('[data-custom-sticky-atc-button-text]');
      this.$titleNodes = Array.from(this.querySelectorAll('[data-custom-sticky-atc-title]'));
      this.$variantNodes = Array.from(this.querySelectorAll('[data-custom-sticky-atc-variant]'));
      this.$saleNodes = Array.from(this.querySelectorAll('[data-custom-sticky-atc-sale]'));
      this.$compareNodes = Array.from(this.querySelectorAll('[data-custom-sticky-atc-compare]'));

      this.baseProductTitle = this.getAttribute('data-product-title') || this.$titleNodes[0]?.textContent?.trim() || '';
      this.productFormSelector = this.getAttribute('data-product-form-selector') || 'form[action*="/cart/add"], form[data-type="add-to-cart-form"], .shopify-product-info__product-form';
      this.productInfoSelector = this.getAttribute('data-product-info-selector') || '.shopify-product-info, [data-product-info]';
      this.addToCartSelector = this.getAttribute('data-add-to-cart-selector') || '[data-add-to-cart], button[type="submit"][name="add"], button[name="add"], button[type="submit"], input[type="submit"]';
      this.cartAddUrl = this.getAttribute('data-cart-add-url') || `${this.getShopifyRoot()}cart/add.js`;
      this.cartUrl = this.getAttribute('data-cart-url') || `${this.getShopifyRoot()}cart`;

      this.footerSelector = this.getAttribute('data-footer-selector') || 'footer, [role="contentinfo"], .shopify-section-group-footer-group, #shopify-section-footer, [id*="shopify-section-footer"], .footer';
      this.footerHideOffset = parseInt(this.getAttribute('data-footer-hide-offset') || this.getAttribute('data-footer-offset') || '480', 10);
      if (!Number.isFinite(this.footerHideOffset) || this.footerHideOffset < 0) this.footerHideOffset = 480;
      this._footerElement = null;

      this.kachingBarSelector = '.kaching-bundles__bar';
      this.kachingSelectedClass = 'kaching-bundles__bar--selected';

      this._realATCButton = null;
      this._seenRealATC = false;
      this._realATCTop = null;
      this._realATCHeight = null;
      this._loadingTimer = null;
      this._hydrateRAF = null;
      this._visibilityRetryTimer = null;
      this._resetTimers = [];
      this._initialSyncTimers = [];
      this._intersectionObserver = null;
      this._lastStateKey = '';

      this._onStickyButtonClick = this.onStickyButtonClick.bind(this);
      this._onDocumentClick = this.onDocumentClick.bind(this);
      this._onDocumentChange = this.onDocumentChange.bind(this);
      this._onDocumentInput = this.onDocumentInput.bind(this);
      this._onVariantChangeEvent = this.onVariantChangeEvent.bind(this);
      this._onCartChangeEvent = this.onCartChangeEvent.bind(this);
      this._onScroll = this.updateStickyVisibility.bind(this);
      this._onResize = this.onWindowResize.bind(this);

      this.$button?.addEventListener('click', this._onStickyButtonClick);

      this.bindLiveListeners();
      this.initStickyVisibility();
      this.runInitialSync();
    }

    disconnectedCallback() {
      this.$button?.removeEventListener('click', this._onStickyButtonClick);

      document.removeEventListener('click', this._onDocumentClick, true);
      document.removeEventListener('change', this._onDocumentChange, true);
      document.removeEventListener('input', this._onDocumentInput, true);
      document.removeEventListener('variant:change', this._onVariantChangeEvent);
      document.removeEventListener('product:variant-change', this._onVariantChangeEvent);
      document.removeEventListener('shopify:section:load', this._onVariantChangeEvent);

      this.getCartEventNames().forEach((eventName) => {
        document.removeEventListener(eventName, this._onCartChangeEvent);
      });

      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);

      this._intersectionObserver?.disconnect();
      window.clearTimeout(this._loadingTimer);
      window.clearTimeout(this._visibilityRetryTimer);
      this._resetTimers.forEach((timer) => window.clearTimeout(timer));
      this._initialSyncTimers.forEach((timer) => window.clearTimeout(timer));

      if (this._hydrateRAF) window.cancelAnimationFrame(this._hydrateRAF);

      this._isMounted = false;
    }

    getShopifyRoot() {
      return window.Shopify?.routes?.root || '/';
    }

    getProductInfoRoot() {
      return document.querySelector(this.productInfoSelector) || document;
    }

    getProductForm() {
      const root = this.getProductInfoRoot();
      return root.querySelector(this.productFormSelector) || document.querySelector(this.productFormSelector);
    }

    getFooterElement() {
      if (this._footerElement && document.contains(this._footerElement)) {
        return this._footerElement;
      }

      const selectors = String(this.footerSelector || '')
        .split(',')
        .map((selector) => selector.trim())
        .filter(Boolean);

      for (const selector of selectors) {
        try {
          const footer = Array.from(document.querySelectorAll(selector)).find((node) => {
            return !this.contains(node) && node.getClientRects().length > 0;
          });

          if (footer) {
            this._footerElement = footer;
            return footer;
          }
        } catch (error) {
          /* ignore invalid custom selector */
        }
      }

      return null;
    }

    isFooterClose() {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const footer = this.getFooterElement();

      if (footer) {
        const rect = footer.getBoundingClientRect();

        return rect.bottom > 0 && rect.top <= viewportHeight + this.footerHideOffset;
      }

      const documentElement = document.documentElement;
      const body = document.body;
      const scrollY = window.scrollY || documentElement.scrollTop || 0;
      const pageHeight = Math.max(
        documentElement.scrollHeight || 0,
        body?.scrollHeight || 0
      );

      if (!pageHeight) return false;

      return pageHeight - (scrollY + viewportHeight) <= this.footerHideOffset;
    }

    getRealATCButton() {
      const form = this.getProductForm();
      if (!form) return null;

      const buttons = Array.from(form.querySelectorAll(this.addToCartSelector)).filter((button) => !this.contains(button));
      return buttons.find((button) => button.offsetParent !== null) || buttons[0] || null;
    }

    getCurrentVariantId() {
      const form = this.getProductForm();
      if (!form) return null;

      const field = form.querySelector('[name="id"]');
      const value = field?.value || field?.getAttribute('value');
      return value ? String(value) : null;
    }

    getCurrentQuantity() {
      const form = this.getProductForm();
      if (!form) return 1;

      const readQuantity = (field) => {
        if (!field) return null;

        const rawValue = field.value || field.getAttribute('value');
        const parsedValue = parseInt(rawValue, 10);

        return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
      };

      const visibleQuantitySelectors = [
        '[data-quantity-input]',
        '.shopify-product-info__quantity-input',
        'quantity-input input[name="quantity"]',
        'input[name="quantity"]:not([type="hidden"])',
        'select[name="quantity"]',
      ];

      for (const selector of visibleQuantitySelectors) {
        const fields = Array.from(form.querySelectorAll(selector)).filter((field) => {
          return !this.contains(field) && field.type !== 'hidden';
        });

        for (const field of fields) {
          const value = readQuantity(field);
          if (value) return value;
        }
      }

      const syncedHiddenQuantitySelectors = [
        '[data-form-quantity]',
        'input[type="hidden"][name="quantity"]',
        'input[name="quantity"]',
      ];

      for (const selector of syncedHiddenQuantitySelectors) {
        const fields = Array.from(form.querySelectorAll(selector)).filter((field) => !this.contains(field));

        for (const field of fields) {
          const value = readQuantity(field);
          if (value) return value;
        }
      }

      const quantityLikeFields = form.querySelectorAll('input[type="hidden"], input:not([type]), select');

      for (const field of quantityLikeFields) {
        const name = (field.getAttribute('name') || '').toLowerCase();
        const id = (field.getAttribute('id') || '').toLowerCase();
        const dataName = Array.from(field.attributes || [])
          .map((attribute) => attribute.name.toLowerCase())
          .join(' ');

        const looksLikeQuantity =
          name.includes('quantity') ||
          name.includes('qty') ||
          id.includes('quantity') ||
          id.includes('qty') ||
          dataName.includes('quantity') ||
          dataName.includes('qty');

        if (!looksLikeQuantity) continue;

        const value = readQuantity(field);
        if (value) return value;
      }

      return 1;
    }

    hasKachingBundle() {
      return !!document.querySelector(this.kachingBarSelector);
    }

    getActiveKachingBar() {
      const selected = document.querySelector(`${this.kachingBarSelector}.${this.kachingSelectedClass}`);
      if (selected) return selected;

      const checkedInput = document.querySelector(`${this.kachingBarSelector} input[type="radio"]:checked`);
      if (checkedInput) return checkedInput.closest(this.kachingBarSelector);

      return document.querySelector(this.kachingBarSelector);
    }

    getKachingDealSettings() {
      const scripts = Array.from(document.querySelectorAll('.kaching-bundles-deal-block-settings'));

      for (const script of scripts) {
        try {
          const data = JSON.parse(script.textContent || '{}');
          if (data && Array.isArray(data.dealBars)) return data;
        } catch (error) {
          /* ignore malformed app JSON */
        }
      }

      return null;
    }

    getKachingDeal(bar) {
      const dealId = bar?.getAttribute('data-deal-bar-id');
      if (!dealId) return null;

      const settings = this.getKachingDealSettings();
      return settings?.dealBars?.find((item) => item.id === dealId) || null;
    }

    getKachingDealQuantity(bar) {
      const deal = this.getKachingDeal(bar);
      if (!deal) return this.getCurrentQuantity();

      if (Number.isFinite(Number(deal.quantity)) && Number(deal.quantity) > 0) {
        return Number(deal.quantity);
      }

      return this.getCurrentQuantity();
    }

    moneyFromText(rawText) {
      const raw = String(rawText || '').replace(/\s+/g, ' ').trim();
      if (!raw) return null;

      const match = raw.match(/-?[\d.,]+/);
      if (!match) return null;

      const numberString = match[0];
      const numberIndex = raw.indexOf(numberString);
      const prefix = raw.slice(0, numberIndex).trim();
      const suffix = raw.slice(numberIndex + numberString.length).trim();
      const lastDot = numberString.lastIndexOf('.');
      const lastComma = numberString.lastIndexOf(',');
      const decimalPosition = Math.max(lastDot, lastComma);

      let normalized = '';

      if (decimalPosition >= 0) {
        const intPart = numberString.slice(0, decimalPosition).replace(/[.,]/g, '');
        const decimalPart = numberString.slice(decimalPosition + 1).replace(/[.,]/g, '');
        normalized = `${intPart}.${decimalPart}`;
      } else {
        normalized = numberString.replace(/[.,]/g, '');
      }

      const value = Number(normalized);
      if (!Number.isFinite(value)) return null;

      return { value, prefix, suffix, raw };
    }

    moneyFromElement(element) {
      if (!element) return null;

      const clone = element.cloneNode(true);
      const unitLabel = clone.querySelector('.kaching-bundles__bar-price__unit-label');
      const hasUnitLabel = !!unitLabel;

      if (unitLabel) unitLabel.remove();

      const parsed = this.moneyFromText(clone.textContent || '');
      if (!parsed) return null;

      return { ...parsed, hasUnitLabel };
    }

    formatMoney(parsed, value) {
      const safeValue = Number.isFinite(value) ? value : 0;
      const amount = safeValue.toFixed(2);
      const prefix = parsed.prefix ? parsed.prefix : '';
      const suffix = parsed.suffix ? ` ${parsed.suffix}` : '';

      return `${prefix}${amount}${suffix}`.trim();
    }

    normalizeVariantLabel(value) {
      const text = String(value || '').replace(/\s+/g, ' ').trim();
      if (!text) return '';
      if (text.toLowerCase() === 'default title') return '';
      if (text.toLowerCase() === this.baseProductTitle.toLowerCase()) return '';
      return text;
    }

    getSimpleProductPriceElement() {
      const root = this.getProductInfoRoot();
      const selectors = [
        '.shopify-product-info__price[data-price]',
        '[data-price]',
        '.price-item--sale',
        '.price__regular .price-item',
        '.price-item',
      ];

      for (const selector of selectors) {
        const element = Array.from(root.querySelectorAll(selector)).find((node) => {
          return !this.contains(node) && (node.textContent || '').trim();
        });

        if (element) return element;
      }

      return null;
    }

    getSimpleProductCompareElement() {
      const root = this.getProductInfoRoot();
      const selectors = [
        '.shopify-product-info__compare-price[data-compare-price]',
        '[data-compare-price]',
        '.price__sale .price-item--regular',
        '.price-item--regular',
      ];

      for (const selector of selectors) {
        const element = Array.from(root.querySelectorAll(selector)).find((node) => {
          return !this.contains(node) && (node.textContent || '').trim();
        });

        if (element) return element;
      }

      return null;
    }

    getProductJsonVariantState() {
      const variantId = this.getCurrentVariantId();
      if (!variantId) return null;

      const scripts = Array.from(document.querySelectorAll('[data-product-json]'));

      for (const script of scripts) {
        try {
          const data = JSON.parse(script.textContent || '{}');
          const variant = data?.variants?.find((item) => String(item.id) === String(variantId));
          if (!variant) continue;

          const optionValues = [variant.option1, variant.option2, variant.option3]
            .map((item) => this.normalizeVariantLabel(item))
            .filter(Boolean);

          return {
            price: variant.price ? this.moneyFromText(variant.price) : null,
            compare: variant.compare_at_price ? this.moneyFromText(variant.compare_at_price) : null,
            variantLabel: optionValues.join(' / '),
          };
        } catch (error) {
          /* ignore malformed product JSON */
        }
      }

      return null;
    }

    getPriceState() {
      if (this.hasKachingBundle()) {
        const activeBar = this.getActiveKachingBar();
        const priceElement = activeBar?.querySelector('.kaching-bundles__bar-price');
        const compareElement = activeBar?.querySelector('.kaching-bundles__bar-full-price');
        const parsed = this.moneyFromElement(priceElement);
        const compareParsed = this.moneyFromElement(compareElement);
        const variantLabel = this.normalizeVariantLabel(activeBar?.querySelector('.kaching-bundles__bar-title')?.textContent);

        if (parsed) {
          const quantity = parsed.hasUnitLabel ? this.getKachingDealQuantity(activeBar) : 1;
          const compareQuantity = compareParsed?.hasUnitLabel ? this.getKachingDealQuantity(activeBar) : 1;

          return {
            title: this.baseProductTitle,
            variantLabel,
            price: this.formatMoney(parsed, parsed.value * quantity),
            comparePrice: compareParsed ? this.formatMoney(compareParsed, compareParsed.value * compareQuantity) : '',
            mode: 'kaching',
          };
        }
      }

      const jsonVariantState = this.getProductJsonVariantState();
      const priceElement = this.getSimpleProductPriceElement();
      const compareElement = this.getSimpleProductCompareElement();
      const parsed = this.moneyFromElement(priceElement) || jsonVariantState?.price || null;
      const compareParsed = this.moneyFromElement(compareElement) || jsonVariantState?.compare || null;

      return {
        title: this.baseProductTitle,
        variantLabel: jsonVariantState?.variantLabel || '',
        price: parsed ? this.formatMoney(parsed, parsed.value) : '',
        comparePrice: compareParsed ? this.formatMoney(compareParsed, compareParsed.value) : '',
        mode: 'simple',
      };
    }

    updateSaleText(text) {
      if (!text) return;

      this.$saleNodes.forEach((node) => {
        if (node.textContent !== text) node.textContent = text;
      });
    }

    updateCompareText(text) {
      this.$compareNodes.forEach((node) => {
        if (text) {
          if (node.textContent !== text) node.textContent = text;
          node.hidden = false;
        } else {
          if (node.textContent !== '') node.textContent = '';
          node.hidden = true;
        }
      });
    }

    updateTitleText(text) {
      const value = text || this.baseProductTitle;
      if (!value) return;

      this.$titleNodes.forEach((node) => {
        if (node.textContent !== value) node.textContent = value;
      });
    }

    updateVariantText(text) {
      const value = this.normalizeVariantLabel(text);

      this.$variantNodes.forEach((node) => {
        if (value) {
          if (node.textContent !== value) node.textContent = value;
          node.hidden = false;
        } else {
          if (node.textContent !== '') node.textContent = '';
          node.hidden = true;
        }
      });
    }

    getButtonDefaultText() {
      return this.$buttonText?.getAttribute('data-default-text') || 'Add to cart';
    }

    getRealButtonText(realButton) {
      return String(realButton?.textContent || realButton?.value || '').replace(/\s+/g, ' ').trim();
    }

    realButtonLooksTemporarilyDisabled(realButton) {
      if (!realButton) return false;

      const text = this.getRealButtonText(realButton).toLowerCase();
      const addingLabel = String(realButton.getAttribute('data-adding-label') || 'adding').toLowerCase();
      const addedLabel = String(realButton.getAttribute('data-added-label') || 'added').toLowerCase();

      return (
        realButton.classList.contains('loading') ||
        realButton.classList.contains('is-loading') ||
        realButton.getAttribute('aria-busy') === 'true' ||
        text.includes(addingLabel) ||
        text.includes(addedLabel)
      );
    }

    realButtonLooksSoldOut(realButton) {
      if (!realButton) return false;

      const isDisabled = realButton.disabled || realButton.getAttribute('aria-disabled') === 'true';
      if (!isDisabled) return false;
      if (this.realButtonLooksTemporarilyDisabled(realButton)) return false;

      const text = this.getRealButtonText(realButton).toLowerCase();
      const soldOutLabel = String(realButton.getAttribute('data-soldout-label') || 'sold out').toLowerCase();

      return (
        text.includes(soldOutLabel) ||
        text.includes('sold out') ||
        text.includes('unavailable') ||
        text.includes('out of stock')
      );
    }

    syncButtonState() {
      if (!this.$button || this.$button.classList.contains('is-loading')) return;

      const realButton = this.getRealATCButton();
      const isSoldOut = this.realButtonLooksSoldOut(realButton);
      const soldOutLabel = realButton?.getAttribute('data-soldout-label') || 'Sold out';

      this.$button.disabled = !!isSoldOut;
      this.$button.setAttribute('aria-disabled', isSoldOut ? 'true' : 'false');

      if (this.$buttonText) {
        this.$buttonText.textContent = isSoldOut ? soldOutLabel : this.getButtonDefaultText();
      }
    }

    hydrate() {
      const state = this.getPriceState();
      const stateKey = JSON.stringify(state || {});

      if (stateKey !== this._lastStateKey) {
        this._lastStateKey = stateKey;

        this.updateTitleText(state?.title || this.baseProductTitle);
        this.updateVariantText(state?.variantLabel || '');

        if (state?.price) {
          this.updateSaleText(state.price);
        }

        this.updateCompareText(state?.comparePrice || '');
      }

      this.syncButtonState();
    }

    scheduleHydrate() {
      if (this._hydrateRAF) window.cancelAnimationFrame(this._hydrateRAF);
      this._hydrateRAF = window.requestAnimationFrame(() => this.hydrate());
    }

    runInitialSync() {
      [0, 250, 700, 1400, 2600, 4200].forEach((delay) => {
        const timer = window.setTimeout(() => {
          this.scheduleHydrate();
          this.updateStickyVisibility();
        }, delay);

        this._initialSyncTimers.push(timer);
      });
    }

    setLoading(isLoading) {
      if (!this.$button) return;

      this.$button.classList.toggle('is-loading', !!isLoading);
      this.$button.disabled = !!isLoading;
      this.$button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');

      if (this.$buttonText) {
        this.$buttonText.textContent = isLoading ? 'Adding…' : this.getButtonDefaultText();
      }

      window.clearTimeout(this._loadingTimer);

      if (isLoading) {
        this._loadingTimer = window.setTimeout(() => this.resetAfterAdd(), 4000);
      }
    }

    resetAfterAdd() {
      if (!this.$button) return;

      window.clearTimeout(this._loadingTimer);
      this.$button.classList.remove('is-loading');
      this.$button.disabled = false;
      this.$button.setAttribute('aria-disabled', 'false');

      if (this.$buttonText) {
        this.$buttonText.textContent = this.getButtonDefaultText();
      }

      this.scheduleHydrate();
      window.setTimeout(() => this.syncButtonState(), 80);
    }

    scheduleResetAfterAdd() {
      this._resetTimers.forEach((timer) => window.clearTimeout(timer));
      this._resetTimers = [250, 700, 1200, 2200, 3500].map((delay) => {
        return window.setTimeout(() => this.resetAfterAdd(), delay);
      });
    }

    showSticky() {
      this.classList.add('custom-sticky-atc--shown');
      this.classList.remove('custom-sticky-atc--hidden');
    }

    hideSticky() {
      this.classList.add('custom-sticky-atc--hidden');
      this.classList.remove('custom-sticky-atc--shown');
      this.resetAfterAdd();
    }

    computeRealATCMetrics() {
      if (!this._realATCButton) return;

      const rect = this._realATCButton.getBoundingClientRect();
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;

      this._realATCTop = rect.top + scrollY;
      this._realATCHeight = rect.height || this._realATCHeight || 0;
    }

    updateStickyVisibility() {
      if (!this._realATCButton || !document.contains(this._realATCButton)) {
        const nextButton = this.getRealATCButton();

        if (nextButton && nextButton !== this._realATCButton) {
          this.observeRealATCButton(nextButton);
        } else {
          this.hideSticky();
          return;
        }
      }

      if (this.isFooterClose()) {
        this.hideSticky();
        return;
      }

      const rect = this._realATCButton.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const realATCVisible = rect.bottom > 0 && rect.top < viewportHeight;
      const realATCAboveViewport = rect.bottom < 0;

      if (realATCVisible) this._seenRealATC = true;

      if (realATCVisible) {
        this.hideSticky();
        return;
      }

      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      const realATCTop = this._realATCTop ?? (rect.top + scrollY);
      const realATCHeight = this._realATCHeight ?? rect.height ?? 0;
      const isBelowRealATC = scrollY > realATCTop + realATCHeight;
      const shouldShow = (this._seenRealATC || isBelowRealATC) && realATCAboveViewport;

      if (shouldShow) this.showSticky();
      else this.hideSticky();
    }

    observeRealATCButton(button) {
      this._realATCButton = button;
      this.computeRealATCMetrics();

      this._intersectionObserver?.disconnect();
      this._intersectionObserver = new IntersectionObserver(() => this.updateStickyVisibility(), {
        root: null,
        threshold: 0,
      });
      this._intersectionObserver.observe(button);

      this.hideSticky();
      this.updateStickyVisibility();
    }

    initStickyVisibility() {
      const setup = () => {
        const button = this.getRealATCButton();

        if (!button) {
          this._visibilityRetryTimer = window.setTimeout(setup, 250);
          return;
        }

        this.observeRealATCButton(button);
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize, { passive: true });
      };

      setup();
    }

    onWindowResize() {
      this.computeRealATCMetrics();
      this.updateStickyVisibility();
    }

    bindLiveListeners() {
      document.addEventListener('click', this._onDocumentClick, true);
      document.addEventListener('change', this._onDocumentChange, true);
      document.addEventListener('input', this._onDocumentInput, true);
      document.addEventListener('variant:change', this._onVariantChangeEvent);
      document.addEventListener('product:variant-change', this._onVariantChangeEvent);
      document.addEventListener('shopify:section:load', this._onVariantChangeEvent);

      this.getCartEventNames().forEach((eventName) => {
        document.addEventListener(eventName, this._onCartChangeEvent);
      });
    }

    getCartEventNames() {
      return [
        'cart:change',
        'cart:updated',
        'cart:refresh',
        'cart-drawer:refresh',
        'cart-drawer:updated',
        'ajaxCart:updated',
        'theme:cart:change',
      ];
    }

    onDocumentClick(event) {
      const target = event.target;

      if (target?.closest?.(this.kachingBarSelector)) {
        this.scheduleHydrate();
        window.setTimeout(() => this.scheduleHydrate(), 80);
        window.setTimeout(() => this.scheduleHydrate(), 180);
        return;
      }

      if (this.isQuantityTrigger(target) || this.isVariantTrigger(target)) {
        this.scheduleHydrate();
        window.setTimeout(() => this.scheduleHydrate(), 120);
      }
    }

    onDocumentChange(event) {
      const target = event.target;
      if (!target?.matches) return;

      if (this.isQuantityTrigger(target) || this.isVariantTrigger(target) || this.isSellingPlanTrigger(target)) {
        this.scheduleHydrate();
        window.setTimeout(() => this.scheduleHydrate(), 120);
        window.setTimeout(() => this.scheduleHydrate(), 260);
      }
    }

    onDocumentInput(event) {
      const target = event.target;
      if (!target?.matches) return;

      if (this.isQuantityTrigger(target) || this.isVariantTrigger(target)) {
        this.scheduleHydrate();
      }
    }

    onVariantChangeEvent() {
      this.scheduleHydrate();
      window.setTimeout(() => this.scheduleHydrate(), 120);
      window.setTimeout(() => this.updateStickyVisibility(), 120);
    }

    onCartChangeEvent() {
      this.scheduleResetAfterAdd();
    }

    isQuantityTrigger(target) {
      if (!target?.closest) return false;

      return !!target.closest([
        '[name="quantity"]',
        '[data-quantity-input]',
        '.quantity__input',
        '.quantity__button',
        'quantity-input',
        'button[name="minus"]',
        'button[name="plus"]',
        '[data-quantity-minus]',
        '[data-quantity-plus]',
        '[data-qty-minus]',
        '[data-qty-plus]',
      ].join(','));
    }

    isVariantTrigger(target) {
      if (!target?.closest) return false;

      return !!target.closest([
        '[name="id"]',
        'select[name="id"]',
        'input[type="radio"][name^="options"]',
        'input[type="radio"][name^="option"]',
        'select[name^="options"]',
        'select[name^="option"]',
        '[data-variant-id-input]',
        '[data-option-value]',
        '[data-variant-picker]',
        'variant-selects',
        'variant-radios',
      ].join(','));
    }

    isSellingPlanTrigger(target) {
      if (!target?.closest) return false;

      return !!target.closest([
        '[name*="selling_plan"]',
        '[data-selling-plan]',
        '[data-loop]',
        '[class*="loop"]',
      ].join(','));
    }

    ensureActiveKachingBundleSelected() {
      const activeBar = this.getActiveKachingBar();
      if (!activeBar) return;

      const radio = activeBar.querySelector('input[type="radio"]');
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('input', { bubbles: true }));
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      activeBar.dispatchEvent(new Event('click', { bubbles: true }));
    }

    onStickyButtonClick(event) {
      event.preventDefault();

      if (this.$button?.disabled || this.$button?.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);

      if (this.hasKachingBundle()) {
        this.ensureActiveKachingBundleSelected();
      }

      const realButton = this.getRealATCButton();

      if (realButton) {
        window.setTimeout(() => {
          realButton.click();
          this.scheduleResetAfterAdd();
        }, this.hasKachingBundle() ? 140 : 30);
        return;
      }

      this.submitCurrentFormViaAjax();
    }

    submitCurrentFormViaAjax() {
      const form = this.getProductForm();

      if (!form) {
        this.resetAfterAdd();
        return;
      }

      const formData = new FormData(form);
      formData.set('quantity', String(this.getCurrentQuantity()));

      fetch(this.cartAddUrl, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to add item to cart');
          return response.json();
        })
        .then((cartItem) => {
          this.dispatchCartEvents(cartItem);
          this.openCartDrawerFallback();
        })
        .catch(() => {
          const formAction = form.getAttribute('action') || this.cartAddUrl.replace('.js', '');
          form.setAttribute('action', formAction.replace('.js', ''));
          form.submit();
        })
        .finally(() => {
          this.scheduleResetAfterAdd();
        });
    }

    dispatchCartEvents(cartItem) {
      const detail = { cartItem, item: cartItem };

      this.getCartEventNames().forEach((eventName) => {
        document.dispatchEvent(new CustomEvent(eventName, { detail }));
      });
    }

    openCartDrawerFallback() {
      const drawer = document.querySelector('cart-drawer');

      if (drawer && typeof drawer.open === 'function') {
        drawer.open(this.$button || null);
        return;
      }

      const candidates = document.querySelectorAll('cart-drawer, #CartDrawer, .cart-drawer, [data-cart-drawer]');
      let opened = false;

      candidates.forEach((node) => {
        node.hidden = false;
        node.removeAttribute('hidden');
        node.removeAttribute('inert');
        node.setAttribute('aria-hidden', 'false');
        node.classList.add('active', 'is-open');
        opened = true;
      });

      if (!opened) {
        window.location.href = this.cartUrl;
      }
    }
  }

  customElements.define('custom-sticky-atc', CustomStickyATC);
})();